import React, { useEffect, useState } from 'react';
import { Col, Form, Row, Spin, Switch, message } from 'antd';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import Box from '../../../components/Box';
import PhoneInput from '../../../components/PhoneInput';
import { setLoading } from '../../../store/loading/actions';
import accountApis from '../../../services/accountApis';
import { IUpdateCallerIdBookingBody } from '../../../services/apisType';
import StationNumberInput from './components/StationNumberInput';

interface ICallerIdBookingResData {
  enabled: boolean;
  receivedPhoneNumber: string | null;
  forwardPhoneNumber: string | null;
  stationNumber: number | null;
}

const CallerIdBookingPage = () => {
  const { id = '' } = useParams();
  const dispatch = useDispatch();
  const [loading, setLoadingForm] = useState(false);
  const [form] = Form.useForm();
  const [initialValues, setInitialValues] = useState<ICallerIdBookingResData | null>(null);

  const initData = async () => {
    if (!id) return;
    try {
      dispatch(setLoading(true));
      const res = await accountApis.getCallerIdBooking(id);
      const data = res?.data?.data as ICallerIdBookingResData;
      if (data) {
        form.setFieldsValue(data);
        setInitialValues(data);
      } else {
        message.error({ content: res.data.message as string, duration: 2 });
      }
    } catch (error: any) {
      message.error({ content: ('System error: ' + error.message) as string, duration: 3 });
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onReset = () => form.setFieldsValue(initialValues);

  const handleFinish = async (values: ICallerIdBookingResData) => {
    if (!id) return;
    setLoadingForm(true);
    try {
      const body: IUpdateCallerIdBookingBody = {
        enabled: values.enabled,
        receivedPhoneNumber: values.receivedPhoneNumber || '',
        forwardPhoneNumber: values.forwardPhoneNumber || '',
        stationNumber: values.stationNumber || 0,
      };
      const res = await accountApis.updateCallerIdBooking(id, body);
      if (res.data.data) {
        message.success({ content: 'Update successfully', duration: 2 });
        await initData();
      } else {
        message.error({ content: res.data.message as string, duration: 2 });
      }
    } catch (error: any) {
      message.error({ content: ('System error: ' + error.message) as string, duration: 3 });
    } finally {
      setLoadingForm(false);
    }
  };

  return (
    <ContentStyled className='gallery-kit'>
      <Row align={'top'}>
        <h1 style={{ flex: 1 }}>Caller ID Booking</h1>
      </Row>
      <Spin size='large' spinning={loading}>
        <CallerIdBookingContainer layout='vertical' form={form} onFinish={handleFinish}>
          <Form.Item noStyle name={'id'}>
            <input hidden />
          </Form.Item>

          <div className='switch-container'>
            <div className='switch-item'>
              <Form.Item noStyle name={'enabled'} valuePropName='checked'>
                <Switch />
              </Form.Item>
              Caller ID Booking
            </div>
          </div>

          <div className='card-config'>
            <Row gutter={[24, 24]}>
              <Col span={12}>
                <Form.Item
                  label={'Received phone number'}
                  name={'receivedPhoneNumber'}
                  rules={[{ required: true, message: 'Please enter received phone number' }]}
                  style={{ marginBottom: 0 }}
                >
                  <PhoneInput placeholder='Please enter...' style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={'Forward phone number'}
                  name={'forwardPhoneNumber'}
                  rules={[{ required: true, message: 'Please enter forward phone number' }]}
                  style={{ marginBottom: 0 }}
                >
                  <PhoneInput placeholder='Please enter...' style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={'Station Number'} name={'stationNumber'} style={{ marginBottom: 0 }}>
                <StationNumberInput id='stationNumber' name='stationNumber' />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <Form.Item shouldUpdate noStyle>
            <Box style={{ textAlign: 'right' }} className='footer-actions'>
              <button type='button' className='btn-reset' onClick={onReset}>
                <span>Cancel</span>
              </button>
              <button type='submit' className='btn-submit'>
                <span>Save</span>
              </button>
            </Box>
          </Form.Item>
        </CallerIdBookingContainer>
      </Spin>
    </ContentStyled>
  );
};

const ContentStyled = styled(Box)`
  &.gallery-kit {
    min-height: 40vh;
  }

  .ant-form-item-label label {
    color: #1d2129;
    font-family: Poppins;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }

  .footer-actions {
    display: flex;
    align-items: center;
    gap: 16px;
    justify-content: flex-end;
    margin-top: 24px;

    button {
      &:hover {
        cursor: pointer;
        opacity: 0.7;
      }
    }

    .btn-reset {
      display: flex;
      width: 170px;
      height: 40px;
      padding: 16px;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      flex-shrink: 0;
      border-radius: 2px;
      border: 1px solid #86909c;
      background: #e3e9ed;

      span {
        color: #566a7f;
        text-align: center;
        font-family: Poppins;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
      }
    }

    .btn-submit {
      display: flex;
      height: 40px;
      width: 170px;
      padding: 16px;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      border-radius: 2px;
      border: 1px solid #008bff;
      background: #008bff;

      span {
        color: #fff;
        text-align: center;
        font-family: Poppins;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
      }
    }
  }
`;

const CallerIdBookingContainer = styled(Form<ICallerIdBookingResData>)`
  .btn-submit {
    &:disabled {
      opacity: 0.6;
    }
  }

  .switch-container {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
  }

  .switch-item {
    display: flex;
    align-items: center;
    gap: 16px;
    color: #1d2129;
    font-family: Poppins;
    font-size: 16px;
    font-weight: 500;
  }

  .card-config {
    margin-bottom: 16px;

    .card-body {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 24px;

      .ant-form-item-label > label {
        &::before {
          display: inline-block !important;
          margin-right: 4px;
          color: #ff4d4f;
          font-size: 16px;
          font-family: SimSun, sans-serif;
          line-height: 1;
          content: '*';
        }
      }

      input,
      .ant-input {
        width: 100%;
        border-radius: 4px;
        border: 1px solid #dadada;
        background: #fff;
        height: 45px;
        font-family: Inter;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        padding: 0 12px;

        &::placeholder {
          color: #86909c;
        }
      }
    }
  }
`;

export default CallerIdBookingPage;
